/**
 * A small client for any OpenAI-compatible chat server: LM Studio by default, but also Ollama,
 * llama.cpp's server, or a hosted API later. It streams the reply as it's generated and collects
 * any tool calls the model makes.
 *
 * The browser talks to your computer directly; nothing goes through split.milesnguyen.com.
 */

export interface LlmSettings {
  /** e.g. "http://localhost:1234/v1" */
  baseUrl: string;
  model: string;
  /** Optional; LM Studio doesn't need one unless you turned on authentication. */
  apiKey: string;
}

export interface ToolCall {
  id: string;
  name: string;
  /** Raw JSON text, as the model wrote it. */
  arguments: string;
}

/** A piece of a message: text, or a picture (as a data: URL) for vision models. */
export type ContentPart =
  { type: "text"; text: string } | { type: "image_url"; image_url: { url: string } };

export interface ChatMessage {
  role: "system" | "user" | "assistant" | "tool";
  content: string | ContentPart[] | null;
  tool_calls?: { id: string; type: "function"; function: { name: string; arguments: string } }[];
  tool_call_id?: string;
}

export interface ChatResult {
  /** The answer, without the model's reasoning. */
  content: string;
  /** The model's thinking, when it shows it (<think> blocks or a reasoning field). */
  reasoning: string;
  toolCalls: ToolCall[];
  /** How long the reply took. */
  ms: number;
}

export interface ChatUpdate {
  content: string;
  reasoning: string;
}

export class LlmError extends Error {
  constructor(
    message: string,
    readonly kind: "unreachable" | "http" | "bad-response",
  ) {
    super(message);
  }
}

/**
 * The API root. LM Studio shows its address without the /v1 part ("http://127.0.0.1:1234"),
 * so a bare address gets /v1 added.
 */
export function apiBase(url: string): string {
  let u = url.trim().replace(/\/+$/, "");
  if (!/^https?:\/\//i.test(u)) u = `http://${u}`;
  try {
    if (new URL(u).pathname === "/") u += "/v1";
  } catch {
    /* leave it; fetch will report the problem */
  }
  return u;
}
const trim = apiBase;

const isSafari = () =>
  typeof navigator !== "undefined" &&
  /Safari\//.test(navigator.userAgent) &&
  !/Chrome|Chromium|Edg\//.test(navigator.userAgent);

function headers(s: LlmSettings): Record<string, string> {
  const h: Record<string, string> = { "content-type": "application/json" };
  if (s.apiKey.trim()) h.authorization = `Bearer ${s.apiKey.trim()}`;
  return h;
}

async function call(url: string, init: RequestInit): Promise<Response> {
  let res: Response;
  try {
    res = await fetch(url, init);
  } catch (e) {
    if ((e as Error).name === "AbortError") throw e;
    throw new LlmError(
      isSafari()
        ? "Safari won't let a website talk to apps on your computer. Open this page in Chrome or Edge instead."
        : `Can't reach the AI server at ${new URL(url).origin}. In LM Studio, open Server Settings and turn on Enable CORS. If the browser asked about devices on your local network, it needs to be allowed.`,
      "unreachable",
    );
  }
  if (!res.ok) {
    let detail = "";
    try {
      const j = (await res.json()) as { error?: { message?: string } | string };
      detail = typeof j.error === "string" ? j.error : (j.error?.message ?? "");
    } catch {
      /* not JSON */
    }
    throw new LlmError(
      `The AI server answered ${res.status}${detail ? `: ${detail}` : ""}`,
      "http",
    );
  }
  return res;
}

/** The models the server has available (LM Studio lists the ones you've downloaded). */
export async function listModels(s: LlmSettings, signal?: AbortSignal): Promise<string[]> {
  const res = await call(`${trim(s.baseUrl)}/models`, { headers: headers(s), signal });
  const j = (await res.json()) as { data?: { id: string }[] };
  return (j.data ?? []).map((m) => m.id).filter((id) => !/embed/i.test(id));
}

/**
 * Sends a conversation and streams the answer. `onUpdate` gets the answer and the reasoning so
 * far, separately, as they arrive. Resolves with both and any tool calls.
 */
export async function chat(
  s: LlmSettings,
  messages: ChatMessage[],
  tools: readonly unknown[],
  onUpdate: (u: ChatUpdate) => void,
  signal?: AbortSignal,
): Promise<ChatResult> {
  const started = Date.now();
  const res = await call(`${trim(s.baseUrl)}/chat/completions`, {
    method: "POST",
    headers: headers(s),
    signal,
    body: JSON.stringify({
      model: s.model || undefined,
      messages,
      tools: tools.length ? tools : undefined,
      tool_choice: tools.length ? "auto" : undefined,
      temperature: 0.3,
      stream: true,
    }),
  });

  // Some servers ignore stream: true and answer with one JSON object.
  if ((res.headers.get("content-type") ?? "").includes("application/json")) {
    const j = (await res.json()) as {
      choices?: {
        message?: {
          content?: string;
          reasoning_content?: string;
          reasoning?: string;
          tool_calls?: { id: string; function: { name: string; arguments: string } }[];
        };
      }[];
    };
    const m = j.choices?.[0]?.message;
    const split = splitThinking(m?.content ?? "");
    const reasoning = [m?.reasoning_content ?? m?.reasoning ?? "", split.thinking]
      .filter(Boolean)
      .join("\n")
      .trim();
    onUpdate({ content: split.visible, reasoning });
    return {
      content: split.visible,
      reasoning,
      ms: Date.now() - started,
      toolCalls: (m?.tool_calls ?? []).map((t) => ({
        id: t.id,
        name: t.function.name,
        arguments: t.function.arguments,
      })),
    };
  }

  if (!res.body) throw new LlmError("The AI server sent an empty reply.", "bad-response");
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let raw = "";
  let reasoningField = "";
  const calls: { id: string; name: string; arguments: string }[] = [];

  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let nl: number;
    while ((nl = buffer.indexOf("\n")) >= 0) {
      const line = buffer.slice(0, nl).trim();
      buffer = buffer.slice(nl + 1);
      if (!line.startsWith("data:")) continue;
      const data = line.slice(5).trim();
      if (data === "[DONE]") continue;
      let chunk: {
        choices?: {
          delta?: {
            content?: string | null;
            reasoning_content?: string | null;
            reasoning?: string | null;
            tool_calls?: {
              index?: number;
              id?: string;
              function?: { name?: string; arguments?: string };
            }[];
          };
        }[];
      };
      try {
        chunk = JSON.parse(data);
      } catch {
        continue;
      }
      const delta = chunk.choices?.[0]?.delta;
      if (!delta) continue;
      const r = delta.reasoning_content ?? delta.reasoning;
      if (r) reasoningField += r;
      if (delta.content) raw += delta.content;
      if (delta.content || r) {
        const split = splitThinking(raw);
        onUpdate({ content: split.visible, reasoning: (reasoningField + split.thinking).trim() });
      }
      for (const tc of delta.tool_calls ?? []) {
        const i = tc.index ?? calls.length;
        const slot = (calls[i] ??= { id: "", name: "", arguments: "" });
        if (tc.id) slot.id = tc.id;
        if (tc.function?.name) slot.name += tc.function.name;
        if (tc.function?.arguments) slot.arguments += tc.function.arguments;
      }
    }
  }
  const split = splitThinking(raw);
  return {
    content: split.visible,
    reasoning: (reasoningField + split.thinking).trim(),
    ms: Date.now() - started,
    toolCalls: calls.filter(Boolean).map((c, i) => ({ ...c, id: c.id || `call_${i}` })),
  };
}

/** Separates a model's reasoning (<think>...</think>, possibly unfinished) from its answer. */
export function splitThinking(raw: string): { visible: string; thinking: string } {
  const parts: string[] = [];
  let visible = raw.replace(/<think>([\s\S]*?)<\/think>/g, (_m, t: string) => {
    parts.push(t.trim());
    return "";
  });
  const open = /<think>([\s\S]*)$/.exec(visible);
  if (open) {
    parts.push(open[1]!.trim());
    visible = visible.slice(0, open.index);
  }
  return { visible: visible.trimStart(), thinking: parts.filter(Boolean).join("\n\n") };
}

/** Hides a model's private reasoning (<think>...</think>), including an unfinished block. */
export function visibleText(raw: string): string {
  return raw
    .replace(/<think>[\s\S]*?<\/think>/g, "")
    .replace(/<think>[\s\S]*$/, "")
    .trimStart();
}
